import { create } from 'zustand';
import type { EntityKind, EntitySummary } from '../lib/types.js';

/**
 * Search state shared between the home box, the results header and the
 * filter row (wireframe §4): the query, the register-vocabulary filter
 * (companies vs business names — the register's own words, not ours), the
 * "active only" switch and the recent queries the home screen offers back.
 * Results themselves are server data and stay in TanStack Query.
 */
export type KindFilter = 'all' | 'company' | 'business_name';

export interface SearchState {
  q: string;
  kind: KindFilter;
  activeOnly: boolean;
  recent: string[];
  setQ: (q: string) => void;
  setKind: (kind: KindFilter) => void;
  toggleActiveOnly: () => void;
  resetFilters: () => void;
  remember: (q: string) => void;
}

export const MAX_RECENT = 5;

export const useSearchStore = create<SearchState>()((set) => ({
  q: '',
  kind: 'all',
  activeOnly: false,
  recent: [],
  setQ: (q) => set({ q }),
  setKind: (kind) => set({ kind }),
  toggleActiveOnly: () => set((s) => ({ activeOnly: !s.activeOnly })),
  resetFilters: () => set({ kind: 'all', activeOnly: false }),
  remember: (q) =>
    set((s) => {
      const trimmed = q.trim();
      if (!trimmed) return {};
      const rest = s.recent.filter((r) => r.toLowerCase() !== trimmed.toLowerCase());
      return { recent: [trimmed, ...rest].slice(0, MAX_RECENT) };
    }),
}));

const isActive = (status: string | null): boolean =>
  (status ?? '').trim().toLowerCase() === 'active';

/** The filter applied to a loaded page of results (pure; unit-tested). */
export function applyFilters(
  rows: readonly EntitySummary[],
  filter: { kind: KindFilter; activeOnly: boolean },
): EntitySummary[] {
  return rows.filter(
    (r) =>
      (filter.kind === 'all' || r.kind === filter.kind) &&
      (!filter.activeOnly || isActive(r.status)),
  );
}

/** The honest join-count line: register hits, tax records, linked pairs. */
export function joinCounts(rows: readonly EntitySummary[]): {
  register: number;
  taxRecords: number;
  linked: number;
  byKind: Partial<Record<EntityKind, number>>;
} {
  const byKind: Partial<Record<EntityKind, number>> = {};
  let taxRecords = 0;
  let linked = 0;
  for (const r of rows) {
    byKind[r.kind] = (byKind[r.kind] ?? 0) + 1;
    if (r.tax.tpin) taxRecords += 1;
    if (r.tax.linked) linked += 1;
  }
  return { register: rows.length, taxRecords, linked, byKind };
}
